/**
 * 🔒 An article's steps (piece 30, *Checklists in the knowledge base*; decisions
 * 12581 and 12582): what Create Checklist proposes and where the working view
 * draws each checklist item. Steps are read from the published words, never
 * stored: headings reading Step 1, Step 2 and so on where the body carries any,
 * else the items of its first ordered list. An article with neither has no
 * steps, and nothing here refuses it.
 *
 * 🔨 A step's key is made from its words, so a step that moves keeps its key and
 * a reworded one does not. The key is what a template item and a job's item
 * carry as `articleStepKey?`, and it is never shown to a person.
 */
import { richTextPlain } from '../controls/richText';
import { sanitizeArticleBody } from './body';

/** One step of an article, in the order the article reads. */
export interface ArticleStep {
  /** The step's key, made from its words and unique within the article. */
  key: string;
  /** The number the article gives it: the heading's own number, or the list's count. */
  number: number;
  /** The step's first sentence, what a proposed checklist item reads. */
  title: string;
}

/** Where the steps were read from: Step headings, the first ordered list, or nowhere. */
export type ArticleStepSource = 'headings' | 'list' | 'none';

export interface ArticleStepReading {
  source: ArticleStepSource;
  steps: ArticleStep[];
}

/** A part of the body as the working view draws it: the article's words, or the place a step's items stand. */
export type ArticleStepPart = { kind: 'html'; html: string } | { kind: 'step'; key: string; number: number };

/** The longest step title a proposal carries, the length of a picture's line. */
export const ARTICLE_STEP_TITLE_MAX = 200;
const KEY_MAX = 60;

const VOID = new Set(['br', 'hr', 'img', 'wbr']);
const TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9]*)\b[^>]*?(\/?)>/g;
const STEP_HEADING = /^\s*step\s*(\d{1,3})\b\s*[:.)\-–—]?\s*([\s\S]*)$/i;

/** One element at the top of the body: its tag, and where it opens, where its own opening tag ends, and where it ends. */
interface TopElement {
  tag: string;
  start: number;
  openEnd: number;
  end: number;
}

/** The elements standing at the top of the markup, in order; text between them is not an element. */
function topElements(html: string): TopElement[] {
  const out: TopElement[] = [];
  let depth = 0;
  let open: TopElement | null = null;
  for (const match of html.matchAll(TAG)) {
    const closing = match[1] === '/';
    const tag = match[2]!.toLowerCase();
    const at = match.index ?? 0;
    const after = at + match[0].length;
    const selfClosing = match[3] === '/' || VOID.has(tag);
    if (closing) {
      depth = Math.max(0, depth - 1);
      if (depth === 0 && open) {
        open.end = after;
        out.push(open);
        open = null;
      }
      continue;
    }
    if (depth === 0) {
      if (selfClosing) {
        out.push({ tag, start: at, openEnd: after, end: after });
        continue;
      }
      open = { tag, start: at, openEnd: after, end: html.length };
    }
    if (!selfClosing) depth += 1;
  }
  if (open) out.push(open);
  return out;
}

/** The first sentence of some words, its stop kept, cut at the longest title a step carries. */
export function firstSentence(words: string): string {
  const flat = words.replace(/\s+/g, ' ').trim();
  const sentence = /^(.+?[.!?])(?:\s|$)/.exec(flat)?.[1] ?? flat;
  return sentence.length > ARTICLE_STEP_TITLE_MAX ? `${sentence.slice(0, ARTICLE_STEP_TITLE_MAX - 1).trimEnd()}…` : sentence;
}

/** A step's key from its words: lower case letters and digits joined by dashes, its number where it has no words. */
export function articleStepKeyOf(title: string, number: number): string {
  const slug = title
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, KEY_MAX)
    .replace(/-+$/g, '');
  return slug || `step-${number}`;
}

/** Each key made unique within the article by a counter, in the order the steps read. */
function uniqueKeys<T extends { key: string }>(steps: T[]): T[] {
  const seen = new Map<string, number>();
  return steps.map((step) => {
    const count = (seen.get(step.key) ?? 0) + 1;
    seen.set(step.key, count);
    return count === 1 ? step : { ...step, key: `${step.key}-${count}` };
  });
}

const plain = (html: string): string => richTextPlain(html).replace(/\s+/g, ' ').trim();

/** A step and the stretch of the markup it covers, which the working view draws its items after. */
interface Span extends ArticleStep {
  start: number;
  end: number;
}

/** 🔒 Step headings: each top-level heading reading Step and a number, running to the next one, or to the next heading as high. */
function headingSpans(html: string, elements: TopElement[]): Span[] {
  const headings = elements
    .map((element, index) => ({ element, index, level: /^h([1-6])$/.exec(element.tag)?.[1] }))
    .filter((row): row is { element: TopElement; index: number; level: string } => row.level !== undefined);
  const spans: Span[] = [];
  headings.forEach((heading, at) => {
    const words = plain(html.slice(heading.element.openEnd, heading.element.end));
    const step = STEP_HEADING.exec(words);
    if (!step) return;
    const level = Number(heading.level);
    // The step runs to the next Step heading, or to the next heading at its own level or higher, or to the end.
    const next = headings.slice(at + 1).find((later) => STEP_HEADING.test(plain(html.slice(later.element.openEnd, later.element.end))) || Number(later.level) <= level);
    const end = next ? next.element.start : html.length;
    const rest = firstSentence(step[2] ?? '');
    const title = rest || firstSentence(plain(html.slice(heading.element.end, end)));
    const number = Number(step[1]);
    spans.push({ key: articleStepKeyOf(title, number), number, title, start: heading.element.start, end });
  });
  return spans;
}

/** One item of a list, and where it stands in the markup. */
interface ListItem {
  html: string;
  start: number;
  end: number;
}

/** The items directly inside one list, in order, a nested list staying inside its item. */
function listItems(html: string, list: TopElement): ListItem[] {
  const inner = html.slice(list.openEnd, list.end).replace(/<\/ol\s*>\s*$/i, '');
  return topElements(inner)
    .filter((element) => element.tag === 'li')
    .map((element) => ({ html: inner.slice(element.start, element.end), start: list.openEnd + element.start, end: list.openEnd + element.end }));
}

/** The number a list opens at, one where it names none. */
function listStart(openTag: string): number {
  const start = /\bstart="(\d{1,4})"/i.exec(openTag)?.[1];
  return start ? Number(start) : 1;
}

/** An item's own words, the words of any list nested inside it left out. */
function itemWords(itemHtml: string): string {
  const nested = itemHtml.search(/<(?:ol|ul)\b/i);
  return plain(nested < 0 ? itemHtml : itemHtml.slice(0, nested));
}

/** 🔒 The first ordered list's items, each one step, numbered from where the list opens. */
function listSpans(html: string, elements: TopElement[]): { list: TopElement; spans: Span[] } | null {
  const list = elements.find((element) => element.tag === 'ol');
  if (!list) return null;
  const opening = listStart(html.slice(list.start, list.openEnd));
  const spans = listItems(html, list)
    .map((item, index) => ({ item, number: opening + index, title: firstSentence(itemWords(item.html)) }))
    .filter((row) => row.title !== '')
    .map<Span>((row) => ({ key: articleStepKeyOf(row.title, row.number), number: row.number, title: row.title, start: row.item.start, end: row.item.end }));
  return spans.length > 0 ? { list, spans } : null;
}

/** The reading behind both answers: the safe body, where its steps came from, and the stretch each covers. */
function read(html: string): { safe: string; source: ArticleStepSource; spans: Span[]; list: TopElement | null } {
  const safe = sanitizeArticleBody(html ?? '');
  const elements = topElements(safe);
  const headings = uniqueKeys(headingSpans(safe, elements));
  if (headings.length > 0) return { safe, source: 'headings', spans: headings, list: null };
  const fromList = listSpans(safe, elements);
  if (fromList) return { safe, source: 'list', spans: uniqueKeys(fromList.spans), list: fromList.list };
  return { safe, source: 'none', spans: [], list: null };
}

/**
 * 🔒 Decision 12582: an article's steps, read from its words. 🔨 Step headings win where the body carries any,
 * so a numbered list inside a step is that step's detail and never a second set of steps.
 */
export function articleSteps(html: string): ArticleStepReading {
  const { source, spans } = read(html);
  return { source, steps: spans.map(({ key, number, title }) => ({ key, number, title })) };
}

/** A list's opening tag numbered from a given item, its letters kept. */
function listOpening(openTag: string, number: number): string {
  const type = /\btype="([aA])"/.exec(openTag)?.[1];
  return `<ol${number > 1 ? ` start="${number}"` : ''}${type ? ` type="${type}"` : ''}>`;
}

/**
 * 🔒 Decision 12581: the body split where each step's items stand, after the step's own words and pictures. A step
 * read from a list stands as that list item alone, keeping its number, so the items draw between its list's items.
 */
export function articleStepParts(html: string): ArticleStepPart[] {
  const { safe, source, spans, list } = read(html);
  const parts: ArticleStepPart[] = [];
  const words = (from: number, to: number) => {
    const part = safe.slice(from, to);
    if (part.trim() !== '') parts.push({ kind: 'html', html: part });
  };
  if (source === 'none') {
    words(0, safe.length);
    return parts;
  }
  if (source === 'headings') {
    let at = 0;
    for (const span of spans) {
      words(at, span.start);
      words(span.start, span.end);
      parts.push({ kind: 'step', key: span.key, number: span.number });
      at = span.end;
    }
    words(at, safe.length);
    return parts;
  }
  const opening = safe.slice(list!.start, list!.openEnd);
  words(0, list!.start);
  for (const span of spans) {
    parts.push({ kind: 'html', html: `${listOpening(opening, span.number)}${safe.slice(span.start, span.end)}</ol>` });
    parts.push({ kind: 'step', key: span.key, number: span.number });
  }
  words(list!.end, safe.length);
  return parts;
}

/** An item the working view places: the article it cites and the step it was made from. */
export interface PlaceableItem {
  articleId?: string | null;
  articleStepKey?: string | null;
}

/** Where each item stands in the working view, and whether an item names a step the article no longer reads. */
export interface ItemPlacement<T> {
  atStep: Record<string, T[]>;
  alsoOnList: T[];
  /** True where an item names a step of this article the words no longer carry: the article moved since the list was made. */
  moved: boolean;
}

/**
 * 🔒 Decision 12581: each item citing this article at the step it was made from, in the list's order. An item that
 * cites no step, cites another article, or names a step the words no longer carry stands under *Also on this list*.
 */
export function placeChecklistItems<T extends PlaceableItem>(steps: readonly ArticleStep[], items: readonly T[], articleId: string): ItemPlacement<T> {
  const keys = new Set(steps.map((step) => step.key));
  const atStep: Record<string, T[]> = {};
  const alsoOnList: T[] = [];
  let moved = false;
  for (const item of items) {
    const key = item.articleStepKey ?? null;
    if (key !== null && item.articleId === articleId && keys.has(key)) {
      (atStep[key] ??= []).push(item);
      continue;
    }
    if (key !== null && item.articleId === articleId) moved = true;
    alsoOnList.push(item);
  }
  return { atStep, alsoOnList, moved };
}
